/**
 * This is the main entrypoint file for the application.
 *
 * When loaded in the client side, the application is rendered in the
 * #root element.
 *
 * When the bundle created from this file is imported in the server
 * side, the exported `renderApp` function can be used for server side
 * rendering.
 *
 * Note that this file is required for the build process.
 */

// Dependency libs
import React from 'react';
import ReactDOMClient from 'react-dom/client';
import { loadableReady } from '@loadable/component';

// Import default styles before other CSS-related modules are imported
// This ensures that the styles in marketplaceDefaults.css are included
// as first ones in the final build CSS build file.
import './styles/marketplaceDefaults.css';

// Configs and store setup
import appSettings from './config/settings';
import defaultConfig from './config/configDefault';
import {
  LoggingAnalyticsHandler,
  GoogleAnalyticsHandler,
  MetaPixelHandler,
} from './analytics/handlers';
import configureStore from './store';

// Utils
import { createInstance, types as sdkTypes } from './util/sdkLoader';
import { mergeConfig } from './util/configHelpers';
import { matchPathname } from './util/routes';
import * as apiUtils from './util/api';
import * as log from './util/log';

// Import relevant global duck files
import { authInfo } from './ducks/auth.duck';
import { fetchAppAssets } from './ducks/hostedAssets.duck';
import { fetchCurrentUser } from './ducks/user.duck';

// Route config
import routeConfiguration from './routing/routeConfiguration';
// App it self
import { ClientApp, renderApp } from './app';

// Environment variable debugging
console.log("🔐 Stripe Key in use:", process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
console.log("🏪 Marketplace ID in use:", process.env.REACT_APP_SHARETRIBE_MARKETPLACE_ID);
console.log("🚚 Shippo Token (first 6 chars):", (process.env.SHIPPO_API_TOKEN || "").slice(0, 6));

const render = (store, shouldHydrate) => {
  // If the server already loaded the auth information, render the app
  // immediately. Otherwise wait for the flag to be loaded and render
  // when auth information is present.
  const state = store.getState();
  const cdnAssetsVersion = state.hostedAssets.version;
  const authInfoLoaded = state.auth.authInfoLoaded;
  const info = authInfoLoaded ? Promise.resolve({}) : store.dispatch(authInfo());

  info
    .then(() => {
      // Ensure that Loadable Components is ready
      // and fetch hosted assets in parallel before initializing the ClientApp
      return Promise.all([
        loadableReady(),
        store.dispatch(fetchAppAssets(defaultConfig.appCdnAssets, cdnAssetsVersion)),
        store.dispatch(fetchCurrentUser()),
      ]);
    })
    .then(([_, fetchedAppAssets, cu]) => {
      const { translations: translationsRaw, ...rest } = fetchedAppAssets || {};
      // We'll handle translations as a separate data.
      // It's given to React Intl instead of pushing to config Context
      const translations = translationsRaw?.data || {};

      // Rest of the assets are considered as hosted configs
      const configEntries = Object.entries(rest);
      const hostedConfig = configEntries.reduce((collectedData, [name, content]) => {
        return { ...collectedData, [name]: content.data || {} };
      }, {});

      if (shouldHydrate) {
        const container = document.getElementById('root');

        ReactDOMClient.hydrateRoot(
          container,
          <ClientApp store={store} hostedTranslations={translations} hostedConfig={hostedConfig} />,
          { onRecoverableError: log.onRecoverableError }
        );
      } else {
        const container = document.getElementById('root');
        const root = ReactDOMClient.createRoot(container);
        root.render(
          <ClientApp store={store} hostedTranslations={translations} hostedConfig={hostedConfig} />
        );
      }
    })
    .catch(e => {
      log.error(e, 'browser-side-render-failed');
    });
};

const setupAnalyticsHandlers = (googleAnalyticsId, metaPixelId) => {
  let handlers = [];

  // Log analytics page views and events in dev mode
  if (appSettings.dev) {
    handlers.push(new LoggingAnalyticsHandler());
  }

  // Add Google Analytics 4 (GA4) handler if tracker ID is found
  if (googleAnalyticsId) {
    if (googleAnalyticsId.indexOf('G-') !== 0) {
      console.warn(
        'Google Analytics 4 (GA4) should have measurement id that starts with "G-" prefix'
      );
    } else {
      handlers.push(new GoogleAnalyticsHandler());
    }
  }

  // Add Meta Pixel handler if a pixel ID is configured. This sends PageView on
  // in-app navigation; the loader and initial PageView live in
  // util/includeScripts.js.
  if (metaPixelId) {
    handlers.push(new MetaPixelHandler());
  }

  return handlers;
};

// If we're in a browser already, render the client application.
if (typeof window !== 'undefined') {
  // DEBUG: cannot be stripped by Terser
  debugger;                                  // DevTools will pause here if the file runs
  window.__BOOT_PROBE__ = 'client-entry';    // Check this in console later
  document.body.setAttribute('data-boot', '1'); // Visible in Elements
  // Optional: very loud (remove after one deploy)
  // alert('BOOT: client entry executing');

  // At the VERY top: boot logging and error handling
  window.onerror = (m, s, l, c, e) => {
    console.error('[BOOT] window.onerror:', m, s, l, c, e);
  };
  console.log('[BOOT] client bundle executing');

  // 1) Instrument unhandled errors (keeps working in prod)
  window.addEventListener('error', e => {
    console.error('[BOOT] window error:', e.message, e.error);
  });
  window.addEventListener('unhandledrejection', e => {
    console.error('[BOOT] unhandledrejection:', e.reason);
  });

  // 2) Validate hydrate target + initial state
  const rootEl = document.getElementById('root'); // or whatever your SSR root id is
  console.log('[BOOT] rootEl exists?', !!rootEl, rootEl?.outerHTML?.slice(0, 120));
  console.log('[BOOT] window state keys:', Object.keys(window || {}));

  // If you expect a specific inline state/config, log it:
  console.log('[BOOT] __INITIAL_STATE__:', window.__INITIAL_STATE__);
  console.log('[BOOT] __APP_CONFIG__:', window.__APP_CONFIG__);

  // set up logger with Sentry DSN client key and environment
  log.setup();

  const baseUrl = appSettings.sdk.baseUrl ? { baseUrl: appSettings.sdk.baseUrl } : {};
  const assetCdnBaseUrl = appSettings.sdk.assetCdnBaseUrl
    ? { assetCdnBaseUrl: appSettings.sdk.assetCdnBaseUrl }
    : {};

  // Verify environment variables are loaded
  console.log('🌐 Flex Client ID in use:', process.env.REACT_APP_SHARETRIBE_SDK_CLIENT_ID);
  if (!process.env.REACT_APP_SHARETRIBE_SDK_CLIENT_ID) {
    throw new Error('clientId must be provided');
  }

  // eslint-disable-next-line no-underscore-dangle
  const preloadedState = window.__PRELOADED_STATE__ || '{}';
  const initialState = JSON.parse(preloadedState, sdkTypes.reviver);
  const sdk = createInstance({
    transitVerbose: appSettings.sdk.transitVerbose,
    clientId: appSettings.sdk.clientId,
    secure: appSettings.usingSSL,
    typeHandlers: apiUtils.typeHandlers,
    ...baseUrl,
    ...assetCdnBaseUrl,
  });

  // Note: on localhost:3000, you need to use environment variable.
  const googleAnalyticsIdFromSSR = initialState?.hostedAssets?.googleAnalyticsId;
  const googleAnalyticsId = googleAnalyticsIdFromSSR || process.env.REACT_APP_GOOGLE_ANALYTICS_ID;
  // Meta Pixel is configured locally (see config/configAnalytics.js), not via
  // the hosted analytics asset.
  const analyticsHandlers = setupAnalyticsHandlers(googleAnalyticsId, defaultConfig.analytics?.metaPixelId);
  const store = configureStore(initialState, sdk, analyticsHandlers);

  require('./util/polyfills');
  
  // Wrap render in try/catch so nothing gets swallowed
  try {
    console.log('[BOOT] about to hydrate');
    render(store, !!window.__PRELOADED_STATE__);
    console.log('[BOOT] hydration done');
  } catch (err) {
    console.error('[BOOT] hydrate failed:', err);
  }

  if (appSettings.dev) {
    // Expose stuff for the browser REPL
    window.app = {
      appSettings,
      defaultConfig,
      sdk,
      sdkTypes,
      store,
    };
  }
}

// Show warning if CSP is not enabled
const CSP = process.env.REACT_APP_CSP;
const cspEnabled = CSP === 'block' || CSP === 'report';

if (CSP === 'report' && process.env.REACT_APP_ENV === 'production') {
  console.warn(
    'Your production environment should use CSP with "block" mode. Read more from: https://www.sharetribe.com/docs/ftw-security/how-to-set-up-csp-for-ftw/'
  );
} else if (!cspEnabled) {
  console.warn(
    "CSP is currently not enabled! You should add an environment variable REACT_APP_CSP with the value 'report' or 'block'. Read more from: https://www.sharetribe.com/docs/ftw-security/how-to-set-up-csp-for-ftw/"
  );
}

// Export the function for server side rendering.
export default renderApp;

// exporting matchPathname and configureStore for server side rendering.
// matchPathname helps to figure out which route is called and if it has preloading needs
// configureStore is used for creating initial store state for Redux after preloading
export {
  matchPathname,
  configureStore,
  routeConfiguration,
  defaultConfig,
  mergeConfig,
  fetchAppAssets,
};
